import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { useApi } from '@/hooks/useApi';
import { useToast } from '@/hooks/use-toast';
import { Map, MapPin } from 'lucide-react';

interface DeliveryZone {
  id: string;
  name: string;
  code: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  driverProfileId: string;
  assignedZoneIds: string[];
}

export function AssignZoneModal({ isOpen, onClose, onSuccess, driverProfileId, assignedZoneIds }: Props) {
  const { get, post } = useApi();
  const { toast } = useToast();

  const [zones, setZones] = useState<DeliveryZone[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchZones = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await get('/api/v1/settings/delivery-zones');
      const data = Array.isArray(res) ? res : res?.data || [];
      setZones(data);
    } catch {
      setZones([]);
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    if (isOpen) {
      fetchZones();
      setSelected([]);
    }
  }, [isOpen, fetchZones]);

  const toggle = (id: string) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const handleSubmit = async () => {
    if (selected.length === 0) {
      toast({ title: 'تنبيه', description: 'اختر منطقة واحدة على الأقل', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const res: any = await post(`/api/v1/drivers/${driverProfileId}/zones`, {
        delivery_zone_ids: selected,
      });
      const data = res?.data || res;
      toast({ title: 'تم بنجاح', description: `تم تعيين ${data?.assigned ?? selected.length} منطقة` });
      onSuccess();
    } catch (err: any) {
      toast({ title: 'خطأ', description: err?.response?.data?.message || 'فشل في تعيين المناطق', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Map className="h-5 w-5" />
            تعيين مناطق توصيل
          </DialogTitle>
          <DialogDescription>اختر المناطق التي يغطيها هذا السائق</DialogDescription>
        </DialogHeader>

        {loading ? (
          <LoadingSkeleton type="card" rows={4} />
        ) : zones.length === 0 ? (
          <EmptyState icon={<MapPin className="h-10 w-10" />} title="لا توجد مناطق" description="لم يتم إنشاء مناطق توصيل بعد" />
        ) : (
          <ScrollArea className="h-64">
            <div className="space-y-2 pe-1">
              {zones.map((zone) => {
                const isAssigned = assignedZoneIds.includes(zone.id);
                const isSelected = selected.includes(zone.id);
                return (
                  <div
                    key={zone.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      isAssigned
                        ? 'bg-muted/50 opacity-60 cursor-not-allowed'
                        : isSelected
                        ? 'border-primary bg-primary/5 cursor-pointer'
                        : 'hover:bg-muted/50 cursor-pointer'
                    }`}
                    onClick={() => !isAssigned && toggle(zone.id)}
                  >
                    <Checkbox checked={isSelected || isAssigned} disabled={isAssigned} onCheckedChange={() => toggle(zone.id)} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{zone.name}</p>
                      {isAssigned && <span className="text-xs text-muted-foreground">معين مسبقاً</span>}
                    </div>
                    {zone.code && <span className="text-xs font-mono text-muted-foreground">{zone.code}</span>}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={submitting}>إلغاء</Button>
          <Button onClick={handleSubmit} disabled={submitting || selected.length === 0}>
            {submitting ? 'جاري التعيين...' : `تعيين (${selected.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
